/**
 * Smart Stable — Premium XLSX sheet builder.
 *
 * Drop-in helpers that produce a uniformly-styled workbook across every
 * export in the project. The look matches the project's gold identity
 * (banner #B79B5B + cream-soft alt rows + thin borders + currency-formatted
 * money columns + freeze panes + autofilter + A4 landscape print setup).
 *
 * REQUIRES the styled SheetJS fork:
 *     npm install xlsx-js-style --legacy-peer-deps
 *
 * Usage:
 *     import * as XLSX from 'xlsx-js-style';
 *     import { appendStyledSheet, saveStyledWorkbook } from '../../utils/xlsxStyled';
 *
 *     const wb = XLSX.utils.book_new();
 *     appendStyledSheet(wb, 'Horse list', rows, 'Stable name · 2026-05-28');
 *     saveStyledWorkbook(wb, `horse-list-${new Date().toISOString().slice(0,10)}.xlsx`);
 */

import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

// ---------------------------------------------------------------------------
// Palette — mirrors smartStableTheme.js (hex without the leading #)
// ---------------------------------------------------------------------------
export const XLSX_PALETTE = {
  gold:       'B79B5B',
  goldSoft:   'EFE4CB',
  cardSoft:   'F7F3EA',
  line:       'EADFCA',
  text:       '4F545C',
  muted:      '7A7F87',
  greenSoft:  'EDF5EE',
  roseSoft:   'F6ECEA',
};

// Header substrings that should be auto-formatted as money (`#,##0.00`).
// Covers EN + AR — extend here if a new vocabulary appears.
const MONEY_KEYWORDS = [
  'amount', 'price', 'revenue', 'expense', 'income', 'total', 'fee', 'cost',
  'prize', 'paid', 'received', 'sar', 'currency', 'balance', 'subtotal',
  'مبلغ', 'سعر', 'إيراد', 'مصروف', 'دخل', 'إجمالي', 'رسوم', 'تكلفة', 'جائزة',
  'مدفوع', 'مستلم', 'رصيد',
];

export function isMoneyHeader(header) {
  const h = String(header || '').toLowerCase();
  return MONEY_KEYWORDS.some((kw) => h.includes(kw));
}

// ---------------------------------------------------------------------------
// Reusable style presets — generated once per call so callers can tweak the
// palette via the optional `palette` argument without mutating module state.
// ---------------------------------------------------------------------------
function buildStyles(palette = XLSX_PALETTE) {
  const thin = (rgb) => ({ style: 'thin', color: { rgb } });
  const border = {
    top:    thin(palette.line),
    bottom: thin(palette.line),
    left:   thin(palette.line),
    right:  thin(palette.line),
  };
  return {
    banner: {
      fill:      { fgColor: { rgb: palette.gold } },
      font:      { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    },
    subtitle: {
      fill:      { fgColor: { rgb: palette.cardSoft } },
      font:      { bold: true, sz: 11, color: { rgb: palette.text } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border,
    },
    header: {
      fill:      { fgColor: { rgb: palette.gold } },
      font:      { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border,
    },
    cell: {
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      font:      { sz: 11, color: { rgb: palette.text } },
      border,
    },
    cellAlt: {
      fill:      { fgColor: { rgb: palette.cardSoft } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      font:      { sz: 11, color: { rgb: palette.text } },
      border,
    },
    money: {
      alignment: { horizontal: 'center', vertical: 'center' },
      font:      { sz: 11, bold: true, color: { rgb: palette.text } },
      border,
      numFmt: '#,##0.00',
    },
    moneyAlt: {
      fill:      { fgColor: { rgb: palette.cardSoft } },
      alignment: { horizontal: 'center', vertical: 'center' },
      font:      { sz: 11, bold: true, color: { rgb: palette.text } },
      border,
      numFmt: '#,##0.00',
    },
    footer: {
      alignment: { horizontal: 'center', vertical: 'center' },
      font:      { italic: true, sz: 9, color: { rgb: palette.muted } },
    },
  };
}

// ---------------------------------------------------------------------------
// Append a fully-styled sheet to an existing workbook.
//
//   workbook    — workbook created via XLSX.utils.book_new()
//   sheetName   — appears in the gold banner AND as the tab title
//   rows        — array of objects; keys become column headers
//   subtitle    — optional cream-row beneath the banner
//   options     — { palette, footerNote }
// ---------------------------------------------------------------------------
export function appendStyledSheet(workbook, sheetName, rows, subtitle = '', options = {}) {
  const palette = options.palette || XLSX_PALETTE;
  const styles = buildStyles(palette);
  const headers = rows.length ? Object.keys(rows[0]) : ['-'];
  const safeRows = rows.length ? rows : [{ '-': '-' }];
  const COLS = headers.length;
  const today = new Date().toISOString().slice(0, 10);
  const generatedAt = options.footerNote || `Generated at: ${new Date().toLocaleString()}`;

  // Layout: banner + subtitle + header + data + blank + footer
  const aoa = [
    [sheetName, ...Array(COLS - 1).fill('')],
    [subtitle || `${sheetName} · ${today}`, ...Array(COLS - 1).fill('')],
    headers,
    ...safeRows.map((row) =>
      headers.map((h) => {
        const raw = row[h];
        if (raw === null || raw === undefined || raw === '') return '';
        if (isMoneyHeader(h)) {
          const n = Number(String(raw).replace(/[^\d.-]/g, ''));
          if (Number.isFinite(n)) return n;
        }
        return raw;
      })
    ),
    Array(COLS).fill(''),
    [generatedAt, ...Array(COLS - 1).fill('')],
  ];

  const sheet = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths — heuristic per header length + content length
  sheet['!cols'] = headers.map((h, idx) => {
    const headerLen = String(h).length;
    let contentMax = 0;
    safeRows.forEach((row) => {
      const v = row[h];
      const len = v == null ? 0 : String(v).length;
      if (len > contentMax) contentMax = len;
    });
    const base = Math.max(headerLen + 4, contentMax + 2);
    const cap  = isMoneyHeader(h) ? 22 : 38;
    return { wch: Math.min(Math.max(base, isMoneyHeader(h) ? 14 : 12), cap) };
  });

  // Row heights
  sheet['!rows'] = aoa.map((_, idx) =>
    idx === 0 ? { hpt: 30 } : idx === 1 ? { hpt: 22 } : { hpt: 20 }
  );

  // Merges: banner / subtitle / footer span the full width
  sheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } },
    { s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: COLS - 1 } },
  ];

  // Freeze the header row + auto-filter on the data range
  sheet['!freeze'] = { xSplit: 0, ySplit: 3 };
  if (rows.length) {
    const lastDataRow = 2 + rows.length;
    const lastColLetter = XLSX.utils.encode_col(COLS - 1);
    sheet['!autofilter'] = { ref: `A3:${lastColLetter}${lastDataRow}` };
  }

  // Cell styling helper
  const setStyle = (ref, style) => {
    if (!sheet[ref]) sheet[ref] = { v: '', t: 's' };
    sheet[ref].s = style;
  };

  setStyle('A1', styles.banner);
  setStyle('A2', styles.subtitle);
  for (let c = 0; c < COLS; c += 1) {
    setStyle(XLSX.utils.encode_cell({ r: 2, c }), styles.header);
  }
  const dataStart = 3;
  const dataEnd = dataStart + safeRows.length - 1;
  for (let r = dataStart; r <= dataEnd; r += 1) {
    const alt = (r - dataStart) % 2 === 1;
    for (let c = 0; c < COLS; c += 1) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const moneyCol = isMoneyHeader(headers[c]);
      const style = moneyCol
        ? (alt ? styles.moneyAlt : styles.money)
        : (alt ? styles.cellAlt : styles.cell);
      setStyle(ref, style);
    }
  }
  setStyle(`A${aoa.length}`, styles.footer);

  // A4 landscape print
  sheet['!pageSetup'] = {
    paperSize: 9,
    orientation: 'landscape',
    fitToWidth: 1,
    fitToHeight: 0,
  };

  XLSX.utils.book_append_sheet(workbook, sheet, sheetName.slice(0, 31));
  return sheet;
}

// ---------------------------------------------------------------------------
// One-shot helper — creates a workbook, appends ONE styled sheet, saves it.
// Use this when a page exports a single sheet (most common case).
// ---------------------------------------------------------------------------
export function exportStyledWorkbook({
  fileName,
  sheetName,
  rows,
  subtitle = '',
  palette,
  footerNote,
}) {
  const wb = XLSX.utils.book_new();
  appendStyledSheet(wb, sheetName, rows, subtitle, { palette, footerNote });
  return saveStyledWorkbook(wb, fileName);
}

// ---------------------------------------------------------------------------
// Write + download an already-built workbook with the styles preserved.
// ---------------------------------------------------------------------------
export function saveStyledWorkbook(workbook, fileName) {
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true,
  });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
  return true;
}

const xlsxStyled = { appendStyledSheet, exportStyledWorkbook, saveStyledWorkbook, isMoneyHeader, XLSX_PALETTE };

export default xlsxStyled;
